import { z } from "zod";
import { SLUG_SAFE_PATTERN } from "@/shared/lib/slugify";

const I18nSchema = z.object({
  en: z.string().optional(),
  lo: z.string().optional(),
  th: z.string().optional(),
});

// --- Sections (admin จัดการส่วนเนื้อหา; หน้าเว็บเป็น static) ---

export const CmsSectionIdParamSchema = z.object({
  id: z.string().min(1),
});

/** Content เป็นได้ทั้ง object (footer ฯลฯ) หรือ array (home.hero, banner) */
const CmsSectionContentSchema = z.union([
  z.record(z.string(), z.unknown()),
  z.array(z.unknown()),
]);

export const CmsSectionCreateSchema = z
  .object({
    pageKey: z.string().min(1, "ຕ້ອງໃສ່ page key (ເຊັ່ນ home)"),
    sectionKey: z.string().min(1, "ຕ້ອງໃສ່ section key"),
    content: CmsSectionContentSchema.optional(),
    sortOrder: z.number().int().min(0).default(0),
  })
  .refine((v) => v.pageKey === "home" && v.sectionKey === "home.content", {
    message: "ສາມາດສ້າງ section ໄດ້ພຽງແຕ່ pageKey=home, sectionKey=home.content",
  })
  .refine(
    (v) => {
      if (v.pageKey !== "home" || v.sectionKey !== "home.content") return true;
      if (v.content == null || Array.isArray(v.content)) return false;
      const result = CmsContentHomeContentSchema.safeParse(v.content);
      return result.success;
    },
    {
      message:
        "ສຳລັບ home.content ຕ້ອງສົ່ງ content ທີ່ຖືກຕ້ອງ (title, displayMode, layoutPreset, products, isActive)",
    },
  );

export const CmsSectionUpdateSchema = z
  .object({
    sectionKey: z.string().min(1).optional(),
    content: CmsSectionContentSchema.optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "No fields to update",
  });

export const CmsSectionReorderSchema = z.object({
  pageKey: z.string().min(1),
  order: z.array(
    z.object({ id: z.string(), sortOrder: z.number().int().min(0) }),
  ),
});

export type CmsSectionCreateInput = z.infer<typeof CmsSectionCreateSchema>;
export type CmsSectionUpdateInput = z.infer<typeof CmsSectionUpdateSchema>;

export interface CmsSectionDTO {
  id: string;
  pageKey: string;
  sectionKey: string;
  content: Record<string, unknown> | unknown[] | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// --- Content schemas สำหรับ home.hero, global.banner, global.footer (typed forms) ---

export const CmsContentHomeHeroItemSchema = z.object({
  mobileImageKey: z.string(),
  tabletImageKey: z.string(),
  desktopImageKey: z.string(),
  order: z.number().int().min(0),
  isActive: z.boolean().default(true),
});
export const CmsContentHomeHeroSchema = z.array(CmsContentHomeHeroItemSchema);
export type CmsContentHomeHeroForm = z.infer<typeof CmsContentHomeHeroSchema>;

export const CmsContentBannerItemSchema = z.object({
  fileKey: z.string(),
  order: z.number().int().min(0),
  isActive: z.boolean().default(true),
});
export const CmsContentBannerSchema = z.array(CmsContentBannerItemSchema);
export type CmsContentBannerForm = z.infer<typeof CmsContentBannerSchema>;

export const CmsContentFooterSocialLinksSchema = z.object({
  facebook: z.string().optional(),
  tiktok: z.string().optional(),
  instagram: z.string().optional(),
  twitter: z.string().optional(),
  youtube: z.string().optional(),
  other: z.string().optional(),
});
export const CmsContentFooterSchema = z.object({
  logoKey: z.string(),
  brandName: z.string(),
  slogan: I18nSchema,
  address: I18nSchema,
  phone: z.string(),
  email: z.string().email().or(z.literal("")),
  socialLinks: z.array(CmsContentFooterSocialLinksSchema),
});
export type CmsContentFooterForm = z.infer<typeof CmsContentFooterSchema>;

export const CmsContentPrivacySchema = z.object({
  content: I18nSchema,
  isActive: z.boolean().default(true),
});
export type CmsContentPrivacyForm = z.infer<typeof CmsContentPrivacySchema>;

export const CmsContentTermsSchema = z.object({
  content: I18nSchema,
  isActive: z.boolean().default(true),
});
export type CmsContentTermsForm = z.infer<typeof CmsContentTermsSchema>;

/** home.content — product block (grid หรือ hero) */
export const LAYOUT_PRESET_OPTIONS = [
  { value: 1 as const, label: "1 ສິນຄ້າ", description: "ສະແດງເຕັມຄວາມກວ້າງ" },
  { value: 2 as const, label: "2 ສິນຄ້າ", description: "2 ຄໍລໍາແຖວ" },
  { value: 3 as const, label: "3 ສິນຄ້າ", description: "3 ຄໍລໍາແຖວ" },
  { value: 4 as const, label: "4 ສິນຄ້າ", description: "4 ຄໍລໍາແຖວ" },
] as const;
export type CmsContentHomeContentLayoutPreset = 1 | 2 | 3 | 4;

export const HOME_CONTENT_DISPLAY_MODE_OPTIONS = [
  {
    value: "grid" as const,
    label: "Grid",
    description: "ສະແດງສິນຄ້າ 1–4 ຊິ້ນໃນ grid",
  },
  {
    value: "hero" as const,
    label: "Hero",
    description: "1 ສິນຄ້າ + artwork ເຕັມພື້ນທີ່",
  },
] as const;
export type CmsContentHomeContentDisplayMode = "grid" | "hero";

const homeContentTitleSchema = I18nSchema.refine(
  (t) =>
    t &&
    [t.lo, t.th, t.en].some(
      (v) => typeof v === "string" && v.trim().length > 0,
    ),
  { message: "ຕ້ອງໃສ່ຫົວຂໍ້ຢ່າງໜ້ອຍຫນຶ່ງພາສາ" },
);

export const CmsContentHomeContentSchema = z
  .object({
    blockType: z.literal("product").default("product"),
    title: homeContentTitleSchema,
    displayMode: z.enum(["grid", "hero"]).default("grid"),
    layoutPreset: z.coerce
      .number()
      .int()
      .pipe(z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])),
    products: z.array(z.string()),
    heroMobileImageKey: z.string().optional(),
    heroTabletImageKey: z.string().optional(),
    heroDesktopImageKey: z.string().optional(),
    /** @deprecated ใช้ heroMobile/Tablet/DesktopImageKey แทน */
    heroArtworkKey: z.string().optional(),
    isActive: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (v.displayMode === "hero") {
      const legacy = v.heroArtworkKey?.trim();
      const mobile = v.heroMobileImageKey?.trim() || legacy;
      const tablet = v.heroTabletImageKey?.trim() || legacy;
      const desktop = v.heroDesktopImageKey?.trim() || legacy;
      if (!mobile) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຕ້ອງອັບໂຫຼດຮູບ Mobile (4:5)",
          path: ["heroMobileImageKey"],
        });
      }
      if (!tablet) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຕ້ອງອັບໂຫຼດຮູບ Tablet (16:9)",
          path: ["heroTabletImageKey"],
        });
      }
      if (!desktop) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຕ້ອງອັບໂຫຼດຮູບ Desktop (8:3)",
          path: ["heroDesktopImageKey"],
        });
      }
      if (v.products.length !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ໂໝດ Hero ຕ້ອງເລືອກສິນຄ້າ 1 ຊິ້ນ",
          path: ["products"],
        });
      }
    }
  });
export type CmsContentHomeContentForm = z.infer<
  typeof CmsContentHomeContentSchema
>;

// --- About section content schemas (typed forms) ---

export const CmsContentAboutHeroSchema = z.object({
  imageKey: z.string(),
  isActive: z.boolean().default(true),
});
export type CmsContentAboutHeroForm = z.infer<typeof CmsContentAboutHeroSchema>;

export const CmsContentAboutStorySchema = z.object({
  imageKey: z.string(),
  title: I18nSchema,
  content: I18nSchema,
  isActive: z.boolean().default(true),
});
export type CmsContentAboutStoryForm = z.infer<
  typeof CmsContentAboutStorySchema
>;

export const CmsContentAboutVisionSchema = z.object({
  imageKey: z.string(),
  title: I18nSchema,
  content: I18nSchema,
  isActive: z.boolean().default(true),
});
export type CmsContentAboutVisionForm = z.infer<
  typeof CmsContentAboutVisionSchema
>;

export const CmsContentAboutMissionItemSchema = z.object({
  icon: z.string(),
  content: I18nSchema,
});
export const CmsContentAboutMissionSchema = z.object({
  title: I18nSchema,
  content: I18nSchema,
  items: z.array(CmsContentAboutMissionItemSchema),
  isActive: z.boolean().default(true),
});
export type CmsContentAboutMissionForm = z.infer<
  typeof CmsContentAboutMissionSchema
>;

export const CmsContentAboutCoreValueItemSchema = z.object({
  imageKey: z.string(),
  title: I18nSchema,
  content: I18nSchema,
  isActive: z.boolean().default(true),
  order: z.number().int().min(0),
});
export const CmsContentAboutCoreValueSchema = z.object({
  title: I18nSchema,
  items: z.array(CmsContentAboutCoreValueItemSchema),
  isActive: z.boolean().default(true),
});
export type CmsContentAboutCoreValueForm = z.infer<
  typeof CmsContentAboutCoreValueSchema
>;

export const CmsContentAboutTeamItemSchema = z.object({
  imageKey: z.string(),
  name: I18nSchema,
  position: I18nSchema,
  isActive: z.boolean().default(true),
  order: z.number().int().min(0),
});
export const CmsContentAboutTeamSchema = z.object({
  items: z.array(CmsContentAboutTeamItemSchema),
  isActive: z.boolean().default(true),
});
export type CmsContentAboutTeamForm = z.infer<typeof CmsContentAboutTeamSchema>;

const CmsSlugSchema = z
  .string()
  .trim()
  .min(1)
  .transform((s) => s.toLowerCase())
  .refine((s) => SLUG_SAFE_PATTERN.test(s), {
    message: "slug ຕ້ອງໃຊ້ຕົວອັກສອນ (ລາວ/ໄທ/ອັງກິດ), ຕົວເລກ ແລະ -",
  });

export const CmsContentAboutShowcaseImageSchema = z.object({
  id: z.string(),
  imageKey: z.string(),
  blurhash: z.string(),
  order: z.number().int().min(0),
});

export const CmsContentAboutShowcaseItemSchema = z.object({
  id: z.string(),
  slug: CmsSlugSchema,
  title: I18nSchema,
  summary: I18nSchema.optional(),
  description: I18nSchema,
  images: z.array(CmsContentAboutShowcaseImageSchema).max(12),
  isActive: z.boolean().default(true),
  order: z.number().int().min(0),
});

export const CmsContentAboutShowcaseSchema = z
  .object({
    items: z.array(CmsContentAboutShowcaseItemSchema),
    isActive: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    const slugs = new Set<string>();
    for (const [i, item] of data.items.entries()) {
      if (slugs.has(item.slug)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "slug ແບບຊ້ຳກັນ",
          path: ["items", i, "slug"],
        });
      }
      slugs.add(item.slug);
    }
  });

export type CmsContentAboutShowcaseForm = z.infer<
  typeof CmsContentAboutShowcaseSchema
>;

export const CmsServiceLinkModeSchema = z.enum(["none", "showcase", "product"]);

export const CmsShowcaseKindSchema = z.enum([
  "production",
  "online-showcase",
]);

export const CmsContentAboutServicesItemSchema = z.object({
  id: z.string(),
  slug: CmsSlugSchema,
  imageKey: z.string(),
  title: I18nSchema,
  content: I18nSchema,
  intro: I18nSchema.optional(),
  linkMode: CmsServiceLinkModeSchema.default("none"),
  showcaseKind: CmsShowcaseKindSchema.optional(),
  productIds: z.array(z.string()).default([]),
  productId: z.string().optional(),
  /** @deprecated legacy CMS showcase ids */
  showcaseItemIds: z.array(z.string()).optional(),
  showcaseItemId: z.string().optional(),
  /** รหัสบริการจาก Stock POS — ใช้ดึงราคา/spec real-time บนเว็บ */
  stockServiceCode: z.string().trim().min(1).optional().nullable(),
  /** แสดงราคาบน About — default true เมื่อมี stockServiceCode */
  showStockPrice: z.boolean().optional(),
  isActive: z.boolean().default(true),
  order: z.number().int().min(0),
});

export const CmsContentAboutServicesSchema = z
  .object({
    items: z.array(CmsContentAboutServicesItemSchema),
    isActive: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    const serviceSlugs = new Set<string>();
    for (const [i, item] of data.items.entries()) {
      if (serviceSlugs.has(item.slug)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "slug ບໍລິການຊ້ຳກັນ",
          path: ["items", i, "slug"],
        });
      }
      serviceSlugs.add(item.slug);

      if (item.showStockPrice === true && !item.stockServiceCode?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຕ້ອງເລືອກບໍລິການ Stock ເມື່ອເປີດສະແດງລາຄາ",
          path: ["items", i, "stockServiceCode"],
        });
      }
    }
  });

export type CmsContentAboutServicesForm = z.infer<
  typeof CmsContentAboutServicesSchema
>;

export const CmsContentAboutPartnerItemSchema = z.object({
  imageKey: z.string(),
  name: I18nSchema,
  website: z.string().optional(),
  isActive: z.boolean().default(true),
  order: z.number().int().min(0),
});
export const CmsContentAboutPartnerSchema = z.object({
  items: z.array(CmsContentAboutPartnerItemSchema),
  isActive: z.boolean().default(true),
});
export type CmsContentAboutPartnerForm = z.infer<
  typeof CmsContentAboutPartnerSchema
>;

export const CmsContentAboutGalleryItemSchema = z.object({
  id: z.string(),
  imageKey: z.string(),
  blurhash: z.string(),
  caption: I18nSchema.optional(),
  isActive: z.boolean().default(true),
  order: z.number().int().min(0),
});
export const CmsContentAboutGallerySchema = z.object({
  title: I18nSchema,
  previewCount: z.number().int().min(1).max(48).default(8),
  items: z.array(CmsContentAboutGalleryItemSchema),
  isActive: z.boolean().default(true),
});
export type CmsContentAboutGalleryForm = z.infer<
  typeof CmsContentAboutGallerySchema
>;

export const CmsContentAboutContactSocialLinksSchema = z.object({
  facebook: z.string().optional(),
  tiktok: z.string().optional(),
  instagram: z.string().optional(),
  twitter: z.string().optional(),
  youtube: z.string().optional(),
  other: z.string().optional(),
});
export const CmsContentAboutContactSchema = z.object({
  title: I18nSchema,
  content: I18nSchema,
  address: I18nSchema,
  phone: z.string(),
  email: z.string().email().or(z.literal("")),
  mapsEmbedCode: z.string(),
  socialLinks: z.array(CmsContentAboutContactSocialLinksSchema),
  isActive: z.boolean().default(true),
});
export type CmsContentAboutContactForm = z.infer<
  typeof CmsContentAboutContactSchema
>;

/** Section keys ທີ່ມີ form เฉพาะ (home.hero, home.content, global.banner, about.*, ...) */
export const CMS_SECTION_KEYS_WITH_FORMS = [
  "home.hero",
  "home.content",
  "global.banner",
  "global.footer",
  "global.privacy",
  "global.terms",
  "about.hero",
  "about.story",
  "about.vision",
  "about.mission",
  "about.core-value",
  "about.team",
  "about.services",
  "about.partner",
  "about.gallery",
  "about.contact",
] as const;
export type CmsSectionKeyWithForm =
  (typeof CMS_SECTION_KEYS_WITH_FORMS)[number];

export function isCmsHomeSectionKeyWithForm(
  key: string,
): key is CmsSectionKeyWithForm {
  return CMS_SECTION_KEYS_WITH_FORMS.includes(key as CmsSectionKeyWithForm);
}

/**
 * Section keys ເລີ່ມຕົ້ນທີ່ບໍ່ສາມາດຍ້າຍຕຳແຫນ່ງ ຫຼື ລຶບໄດ້
 * (home.content ບໍ່ຢູ່ໃນນີ້ ເພາະສາມາດສ້າງ/ລຶບໄດ້)
 */
export const FIXED_CMS_SECTION_KEYS = CMS_SECTION_KEYS_WITH_FORMS.filter(
  (k) => k !== "home.content",
);

export function isFixedCmsSectionKey(key: string): boolean {
  return FIXED_CMS_SECTION_KEYS.includes(
    key as (typeof FIXED_CMS_SECTION_KEYS)[number],
  );
}

/** Section keys ທີ່ບໍ່ສະແດງໃນ CMS settings (deprecated / ຍ້າຍໄປ module ອື່ນ) */
export const HIDDEN_CMS_SECTION_KEYS = ["about.showcase"] as const;

export function isHiddenCmsSectionKey(key: string): boolean {
  return HIDDEN_CMS_SECTION_KEYS.includes(
    key as (typeof HIDDEN_CMS_SECTION_KEYS)[number],
  );
}
